/**
 * useSiteChecklists — React Query hooks for the Trello checklist mirror.
 * Returns an array of `SiteChecklist`, each pre-loaded with its `items[]`.
 *
 * Cache keys are nested under `siteKeys.detail(siteId)` so invalidating a
 * site cascades to its checklists.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { siteChecklists } from '../api/methods';
import { siteKeys } from './useSites';
export const siteChecklistKeys = {
    all: ['siteChecklists'],
    list: (siteId) => [...siteKeys.detail(String(siteId)), 'checklists'],
};
export function useSiteChecklists(siteId) {
    return useQuery({
        queryKey: siteChecklistKeys.list(siteId),
        queryFn: () => siteChecklists.list(siteId),
        enabled: siteId !== '' && siteId != null,
    });
}
export function useUpdateSiteChecklistItem(siteId) {
    const queryClient = useQueryClient();
    const queryKey = siteChecklistKeys.list(siteId);
    return useMutation({
        mutationFn: ({ itemId, state }) => siteChecklists.updateItemState(siteId, itemId, state),
        onMutate: async ({ itemId, state }) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (current) => (current || []).map((checklist) => ({
                ...checklist,
                items: (checklist.items || []).map((item) => item.id === itemId ? { ...item, state } : item),
            })));
            return { previous };
        },
        onError: (_err, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
        },
        onSuccess: (updatedItem) => {
            queryClient.setQueryData(queryKey, (current) => (current || []).map((checklist) => ({
                ...checklist,
                items: (checklist.items || []).map((item) => item.id === updatedItem.id ? updatedItem : item),
            })));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
}
//# sourceMappingURL=useSiteChecklists.js.map